import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { Box, Checkbox, IconButton } from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS, ipdCardSx } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import IpdTabBar from "../../ipd/utils/IpdTabBar";
import IpdConfirmDialog from "../../ipd/utils/IpdConfirmDialog";
import SvgIcon from "../SvgIcon";
import { toBillingError } from "../billing-api";
import {
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  listClassifications,
  createClassification,
  updateClassification,
  deleteClassification,
  listServiceTypes,
  createServiceType,
  updateServiceType,
  deleteServiceType,
  listSampleTypes,
  createSampleType,
  updateSampleType,
  deleteSampleType,
} from "../service-master-api";
import ServiceMasterMasterFormModal from "./ServiceMasterMasterFormModal";

// "47 services · 3 classifications" — what would be left pointing at a master if
// it were retired. A null count means the endpoint did not report one.
function usageLabel(row) {
  const parts = [];
  if (row.serviceCount != null) parts.push(`${row.serviceCount} service${row.serviceCount === 1 ? "" : "s"}`);
  if (row.classificationCount != null) {
    parts.push(`${row.classificationCount} classification${row.classificationCount === 1 ? "" : "s"}`);
  }
  return parts.length ? parts.join(" · ") : "—";
}

// Which endpoint each tab reads, and whether it has a server-side search. Kept
// out of the tab config below so reloading the list does not depend on the group
// dropdown's contents.
const LOADERS = {
  groups: (params) => listGroups(params),
  classifications: (params) => listClassifications(params),
  serviceTypes: ({ activeOnly }) => listServiceTypes({ activeOnly }),
  sampleTypes: (params) => listSampleTypes(params),
};

const SEARCHABLE = { groups: true, classifications: true, serviceTypes: false, sampleTypes: true };

const codeCell = (p) => (
  <Box component="span" sx={{ fontSize: "13px", fontWeight: 800, color: C.primary, whiteSpace: "nowrap" }}>
    {p.value || "—"}
  </Box>
);

const nameCell = (p) => <Box component="span" sx={{ fontSize: "13px", color: C.text }}>{p.value || "—"}</Box>;

const subCell = (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value || "—"}</Box>;

const usageCell = (p) => (
  <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{usageLabel(p.row)}</Box>
);

const statusCell = (p) => (
  <Box component="span" sx={{ padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, background: p.value ? IPD_COLORS.success.bg : IPD_COLORS.pageBg, color: p.value ? IPD_COLORS.success.text : IPD_COLORS.textSecondary }}>
    {p.value ? "Active" : "Inactive"}
  </Box>
);

/**
 * Manage masters — edit, deactivate and bring back the four service-master
 * lookups a service is filed under: group, classification, service type (the
 * part of the bill) and sample type.
 *
 * The Service Master screen's quick-add buttons only ever created these; a code
 * typed wrong or a list grown stale had no way back out of the UI even though
 * every endpoint was already there. Swapped in place of the service grid, same
 * as the Add/Edit service screen, with its own back link.
 */
export default function ServiceMasterMastersPanel({ serviceTypesSupported = true, onBack, onChanged }) {
  const [tab, setTab] = useState("groups");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  // The parent-group dropdown on a classification. Loaded here rather than taken
  // from the screen's lookups so it is right straight after a group is renamed.
  const [groups, setGroups] = useState([]);
  const [formTarget, setFormTarget] = useState(null); // null | { mode: "create" } | { mode: "edit", row }
  const [confirm, setConfirm] = useState(null); // { row, description, force }
  const [confirming, setConfirming] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const tabs = useMemo(
    () => [
      { id: "groups", label: "Groups" },
      { id: "classifications", label: "Classifications" },
      // Hidden on a hospital that has not been given the service type list yet,
      // exactly as the filter and the grid column are.
      ...(serviceTypesSupported ? [{ id: "serviceTypes", label: "Service types" }] : []),
      { id: "sampleTypes", label: "Sample types" },
    ],
    [serviceTypesSupported]
  );

  const config = useMemo(() => {
    const groupOptions = groups.map((g) => ({ id: g.id, name: g.name }));
    return {
      groups: {
        noun: "group",
        title: "Service group",
        emptyMessage: "No service groups",
        emptyDescription: "A group is the top level a service is filed under — Laboratory, Radiology, Pharmacy.",
        columns: [
          { field: "code", headerName: "Code", width: 150, sortable: false, renderCell: codeCell },
          { field: "name", headerName: "Name", flex: 1.4, minWidth: 180, sortable: false, renderCell: nameCell },
          { field: "description", headerName: "Description", flex: 1.2, minWidth: 160, sortable: false, renderCell: subCell },
          { field: "usage", headerName: "Filed under it", width: 210, sortable: false, renderCell: usageCell },
        ],
        fields: (row) => [
          { key: "code", label: "Group code", placeholder: "ONCOLOGY", required: true, initial: row?.code === "—" ? "" : row?.code,
            helper: row ? "Changing the code changes what corporate CATEGORY rules match on." : undefined },
          { key: "name", label: "Name", placeholder: "Oncology", required: true, initial: row?.name },
          { key: "description", label: "Description", placeholder: "Cancer services", initial: row?.description },
        ],
        create: (form) => createGroup({ group_code: form.code.trim(), name: form.name.trim(), description: form.description?.trim() || "" }),
        update: (row, form) => updateGroup(row.id, { group_code: form.code.trim(), name: form.name.trim(), description: form.description?.trim() || "" }),
        remove: (row, opts) => deleteGroup(row.id, opts),
        reactivate: (row) => updateGroup(row.id, { is_active: true }),
      },

      classifications: {
        noun: "classification",
        title: "Classification",
        emptyMessage: "No classifications",
        emptyDescription: "A classification sits inside a group — Hematology inside Laboratory, CT inside Radiology.",
        columns: [
          { field: "code", headerName: "Code", width: 150, sortable: false, renderCell: codeCell },
          { field: "name", headerName: "Name", flex: 1.4, minWidth: 170, sortable: false, renderCell: nameCell },
          { field: "groupName", headerName: "Group", flex: 1, minWidth: 140, sortable: false, renderCell: subCell },
          { field: "usage", headerName: "Filed under it", width: 150, sortable: false, renderCell: usageCell },
        ],
        fields: (row) => [
          { key: "group_id", label: "Group", select: true, options: groupOptions, required: true, initial: row?.groupId,
            helper: row ? "Moving it to another group re-files its services into that group too." : undefined },
          { key: "code", label: "Classification code", placeholder: "PET", required: true, initial: row?.code === "—" ? "" : row?.code },
          { key: "name", label: "Name", placeholder: "PET Scan", required: true, initial: row?.name },
        ],
        note: (row) =>
          row
            ? "A service carries both its group and its classification. Moving this classification moves every service under it into the new group as well, which also changes what corporate CATEGORY rules match on for them."
            : null,
        create: (form) => createClassification({ group_id: form.group_id, classification_code: form.code.trim(), name: form.name.trim() }),
        update: async (row, form) => {
          const saved = await updateClassification(row.id, {
            service_group_id: form.group_id,
            classification_code: form.code.trim(),
            name: form.name.trim(),
          });
          if (saved.servicesRefiled) {
            toast.info(`${saved.servicesRefiled} service(s) re-filed into the new group`);
          }
          return saved;
        },
        remove: (row, opts) => deleteClassification(row.id, opts),
        reactivate: (row) => updateClassification(row.id, { is_active: true }),
      },

      serviceTypes: {
        noun: "service type",
        title: "Service type",
        emptyMessage: "No service types",
        emptyDescription: "A service type is a heading on the printed bill — Bed Charges, OT, Investigations.",
        columns: [
          { field: "name", headerName: "Name", flex: 1.6, minWidth: 200, sortable: false, renderCell: nameCell },
          { field: "usage", headerName: "Filed under it", width: 150, sortable: false, renderCell: usageCell },
        ],
        fields: (row) => [
          { key: "name", label: "Name", placeholder: "Investigations", required: true, initial: row?.name,
            helper: row ? "This is the heading the service prints under on the bill." : undefined },
        ],
        create: (form) => createServiceType({ name: form.name.trim() }),
        update: (row, form) => updateServiceType(row.id, { name: form.name.trim() }),
        remove: (row, opts) => deleteServiceType(row.id, opts),
        reactivate: (row) => updateServiceType(row.id, { is_active: true }),
      },

      sampleTypes: {
        noun: "sample type",
        title: "Sample type",
        emptyMessage: "No sample types",
        emptyDescription: "The specimen a lab service is collected into — EDTA Whole Blood, Serum, Urine.",
        columns: [
          { field: "code", headerName: "Code", width: 200, sortable: false, renderCell: codeCell },
          { field: "name", headerName: "Name", flex: 1.4, minWidth: 180, sortable: false, renderCell: nameCell },
          { field: "usage", headerName: "Collected for", width: 150, sortable: false, renderCell: usageCell },
        ],
        fields: (row) => [
          { key: "name", label: "Name", placeholder: "EDTA Whole Blood", required: true, initial: row?.name },
          { key: "code", label: "Sample code", placeholder: "EDTA-WHOLE-BLOOD", required: true, initial: row?.code === "—" ? "" : row?.code },
        ],
        create: (form) => createSampleType({ name: form.name.trim(), sample_code: form.code.trim() }),
        update: (row, form) => updateSampleType(row.id, { name: form.name.trim(), sample_code: form.code.trim() }),
        remove: (row, opts) => deleteSampleType(row.id, opts),
        reactivate: (row) => updateSampleType(row.id, { is_active: true }),
      },
    };
  }, [groups]);

  const active = config[tab];

  const loadGroups = useCallback(async () => {
    try {
      setGroups(await listGroups({ activeOnly: true }));
    } catch (err) {
      toast.error(toBillingError(err, "Could not load groups"));
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // Debounce search 300ms, same as the service grid.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { activeOnly: !includeInactive };
      if (SEARCHABLE[tab] && search) params.search = search;
      setRows(await LOADERS[tab](params));
    } catch (err) {
      setLoadError(toBillingError(err, "Could not load the list"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, search, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const switchTab = (next) => {
    setTab(next);
    setSearchInput("");
    setSearch("");
    setRows([]);
  };

  // Every save can change a name a dropdown elsewhere on the screen is showing,
  // so the screen behind reloads its lookups too.
  const afterChange = async () => {
    await Promise.all([load(), loadGroups()]);
    await onChanged?.();
  };

  const askDeactivate = (row) => {
    const attached = (row.serviceCount || 0) + (row.classificationCount || 0);
    setConfirm({
      row,
      force: attached > 0,
      description: attached
        ? `${usageLabel(row)} are filed under "${row.name}". They keep working and stay on bills already raised — the ${active.noun} just drops out of the filters and the Add service dropdowns.`
        : `Nothing is filed under "${row.name}". It will drop out of the filters and the Add service dropdowns, and you can bring it back from here.`,
    });
  };

  const runDeactivate = async () => {
    const { row, force } = confirm;
    setConfirming(true);
    try {
      await active.remove(row, { force });
      toast.success(`${row.name} deactivated`);
      setConfirm(null);
      await afterChange();
    } catch (err) {
      // The server found something attached that this page's counts had not. Keep
      // the dialog open with what it said, and let the next click carry force.
      if (err?.requiresForce) {
        setConfirm({ row, force: true, description: toBillingError(err, "Still in use") });
      } else {
        toast.error(toBillingError(err, `Could not deactivate the ${active.noun}`));
        setConfirm(null);
      }
    } finally {
      setConfirming(false);
    }
  };

  const reactivate = async (row) => {
    setBusyId(row.id);
    try {
      await active.reactivate(row);
      toast.success(`${row.name} reactivated`);
      await afterChange();
    } catch (err) {
      toast.error(toBillingError(err, `Could not reactivate the ${active.noun}`));
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    ...active.columns,
    { field: "active", headerName: "Status", width: 100, sortable: false, renderCell: statusCell },
    { field: "actions", headerName: "", width: 150, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IpdButton variant="outlined" size="small" onClick={() => setFormTarget({ mode: "edit", row: p.row })} startIcon={<SvgIcon name="edit" size={13} color={C.text} />}>
            Edit
          </IpdButton>
          {p.row.active ? (
            <IconButton size="small" onClick={() => askDeactivate(p.row)} disabled={busyId === p.row.id} title="Deactivate" sx={{ ml: "6px" }}>
              <SvgIcon name="trash" size={15} color={C.danger} />
            </IconButton>
          ) : (
            <IpdButton variant="text" size="small" onClick={() => reactivate(p.row)} disabled={busyId === p.row.id} sx={{ ml: "4px", minWidth: 0 }}>
              Restore
            </IpdButton>
          )}
        </Box>
      ) },
  ];

  const editing = formTarget?.mode === "edit" ? formTarget.row : null;

  return (
    <div style={{ animation: "ipdFadeIn 0.2s ease" }}>
      <style>{`@keyframes ipdFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>

      <IpdButton variant="outlined" onClick={onBack} startIcon={<SvgIcon name="back" size={16} color={C.text} />} sx={{ mb: "18px" }}>
        Back to Service Master
      </IpdButton>

      <Box sx={{ ...ipdCardSx, mb: "16px" }}>
        <div style={{ padding: "18px 22px" }}>
          <div style={{ fontSize: "17px", fontWeight: 800, color: C.text }}>Manage masters</div>
          <div style={{ fontSize: "12px", color: C.textSub, marginTop: "4px" }}>
            The lists a service is filed under. Renaming one here renames it everywhere it is
            shown; deactivating one takes it out of the filters and the Add service dropdowns
            without touching the services themselves.
          </div>
        </div>
      </Box>

      <IpdTabBar tabs={tabs} activeTab={tab} onChange={switchTab} ariaLabel="Service master lists" />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "14px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "14px", flexWrap: "wrap" }}>
          {SEARCHABLE[tab] && (
            <div style={{ width: "220px" }}>
              <IpdFormField label="Search">
                <IpdTextField fullWidth value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="name or code…" />
              </IpdFormField>
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: C.text, cursor: "pointer", paddingBottom: "8px" }}>
            <Checkbox size="small" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} sx={{ p: 0 }} />
            Include deactivated
          </label>
          <div style={{ fontSize: "13px", color: C.textSub, paddingBottom: "8px" }}>
            {loading ? "Loading…" : `${rows.length} ${active.noun}${rows.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <IpdButton onClick={() => setFormTarget({ mode: "create" })} startIcon={<SvgIcon name="plus" size={15} color={IPD_COLORS.white} />}>
          Add {active.noun}
        </IpdButton>
      </div>

      <IpdDataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        error={loadError}
        onRetry={load}
        getRowId={(r) => r.id}
        disableColumnMenu
        emptyMessage={active.emptyMessage}
        emptyDescription={active.emptyDescription}
        emptyAction={
          <IpdButton onClick={() => setFormTarget({ mode: "create" })} startIcon={<SvgIcon name="plus" size={15} color={IPD_COLORS.white} />}>
            Add {active.noun}
          </IpdButton>
        }
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        getRowClassName={(p) => (p.row.active ? "" : "ipd-row-inactive")}
        sx={{ border: "none", "& .ipd-row-inactive": { opacity: 0.55 } }}
      />

      {formTarget && (
        <ServiceMasterMasterFormModal
          key={`${tab}-${editing?.id || "new"}`}
          title={`${editing ? "Edit" : "Add"} ${active.title.toLowerCase()}`}
          fields={active.fields(editing)}
          note={active.note?.(editing)}
          submitLabel={editing ? "Save changes" : "Add"}
          onClose={() => setFormTarget(null)}
          onSubmit={async (form) => {
            if (editing) {
              await active.update(editing, form);
              toast.success(`${form.name?.trim() || editing.name} updated`);
            } else {
              await active.create(form);
              toast.success(`${active.title} "${form.name?.trim()}" added`);
            }
            await afterChange();
          }}
        />
      )}

      <IpdConfirmDialog
        open={Boolean(confirm)}
        destructive
        title={`Deactivate this ${active.noun}?`}
        description={confirm?.description || ""}
        confirmLabel={confirm?.force ? "Deactivate anyway" : "Deactivate"}
        loading={confirming}
        onConfirm={runDeactivate}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
